import React, { useEffect, useState } from "react";
import { Box, Container, Typography, Button, Alert } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../config/firebase";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { styles } from "./AuthPage.styles";
import PasswordReset from "./PasswordReset";

const AuthActionHandler = () => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const actionCode = searchParams.get("oobCode");
  const continueUrl = searchParams.get("continueUrl");

  useEffect(() => {
    const handleAction = async () => {
      if (!actionCode || !mode) {
        setError("Invalid action link");
        return;
      }

      setLoading(true);
      setError(""); // Clear any existing errors
      setSuccess(false); // Clear any existing success state

      try {
        switch (mode) {
          case "verifyEmail":
            try {
              await applyActionCode(auth, actionCode);
              setSuccess(true);
              // Clear verification state
              localStorage.removeItem("needsVerification");
              localStorage.removeItem("pendingVerificationEmail");
              localStorage.removeItem("pendingVerificationPassword");
              // Redirect to login after 5 seconds
              setTimeout(() => {
                navigate("/auth", {
                  state: {
                    verified: true,
                    showVerificationPage: false,
                  },
                });
              }, 5000);
            } catch (verifyError) {
              console.error("Email verification error:", verifyError);
              setError(getFirebaseErrorMessage(verifyError));
              return; // Exit early on error
            }
            break;

          case "resetPassword":
            try {
              const email = await verifyPasswordResetCode(auth, actionCode);
              setEmail(email);
            } catch (resetError) {
              console.error("Password reset verification error:", resetError);
              setError(getFirebaseErrorMessage(resetError));
              return; // Exit early on error
            }
            break;

          case "recoverEmail":
            try {
              await applyActionCode(auth, actionCode);
              setSuccess(true);
              // Clear verification state
              localStorage.removeItem("needsVerification");
              localStorage.removeItem("pendingVerificationEmail");
              localStorage.removeItem("pendingVerificationPassword");
              setTimeout(() => {
                navigate("/auth", {
                  state: {
                    recovered: true,
                    showVerificationPage: false,
                  },
                });
              }, 5000);
            } catch (recoverError) {
              console.error("Email recovery error:", recoverError);
              setError(getFirebaseErrorMessage(recoverError));
              return; // Exit early on error
            }
            break;

          default:
            setError("Invalid action mode");
        }
      } catch (err) {
        console.error("Action handler error:", err);
        setError(getFirebaseErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    handleAction();
  }, [actionCode, mode, navigate]);

  const handlePasswordReset = async (newPassword) => {
    try {
      setLoading(true);
      await confirmPasswordReset(auth, actionCode, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/auth");
      }, 3000);
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getFirebaseErrorMessage = (error) => {
    const errorCode = error.code;
    const errorMessages = {
      "auth/expired-action-code":
        "This action link has expired. Please request a new one.",
      "auth/invalid-action-code":
        "Invalid action link. Please request a new one.",
      "auth/weak-password":
        "Password is too weak. Please use a stronger password.",
      "auth/invalid-action-code":
        "The action code is invalid or has already been used. Please request a new verification email.",
      "auth/expired-action-code":
        "The action code has expired. Please request a new verification email.",
    };

    return (
      errorMessages[errorCode] ||
      `An unexpected error occurred: ${error.message}. Please try again.`
    );
  };

  if (!actionCode || !mode) {
    return (
      <Box sx={styles.container}>
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <Typography color="error">
            Invalid action link. Please request a new one.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/auth")}
            sx={{ mt: 2 }}
          >
            Back to Sign In
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Container maxWidth="sm" sx={{ textAlign: "center" }}>
        {error && !success && (
          <Box>
            <Alert severity="error" sx={styles.errorAlert}>
              {error}
            </Alert>
            <Button
              variant="text"
              onClick={() => navigate("/auth")}
              sx={{ mb: 2 }}
            >
              Back to Sign In
            </Button>
          </Box>
        )}
        {success && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {mode === "verifyEmail" && (
                <Typography>
                  Email verified successfully! You can now sign in.
                </Typography>
              )}
              {mode === "resetPassword" &&
                "Password reset successfully! Redirecting to login..."}
              {mode === "recoverEmail" &&
                "Email recovered successfully! Redirecting to login..."}
            </Alert>
            <Button
              variant="text"
              onClick={() => navigate("/auth")}
              sx={{ mb: 2 }}
            >
              Back to Sign In
            </Button>
          </Box>
        )}
        {mode === "resetPassword" && !success && !error && <PasswordReset />}
      </Container>
    </Box>
  );
};

export default AuthActionHandler;
